/**
 * synthParams — pure mapping from a ToneDesc to flat synth params.
 *
 * Encodes the exact envelope the retired legacy `SoundManager.playSFX` baked in (0.15 gain
 * headroom, exponential decay to 0.001, frequency sweep to `freq * 0.3` floored at
 * 20Hz) so the cue migration is behavior-identical AND the math is unit-testable
 * without an AudioContext. The AudioEngine plumbing (later) just reads these.
 */

import type { ToneDesc, ToneParams } from './audioTypes';

/** Legacy constants lifted verbatim from playSFX — named, not magic. */
export const LEGACY_GAIN_HEADROOM = 0.15; // playSFX multiplied volume by this
export const DECAY_FLOOR_GAIN = 0.001;    // exponentialRampToValueAtTime target
export const SWEEP_RATIO = 0.3;           // freq sweeps toward freq * 0.3 ...
export const SWEEP_FLOOR_HZ = 20;         // ... floored at 20Hz

/** Shift a frequency by a number of (possibly fractional) semitones. */
export function applyPitchJitter(freq: number, semitones: number): number {
  return freq * Math.pow(2, semitones / 12);
}

/**
 * Resolve a ToneDesc into the flat params the synth plumbing schedules.
 * @param gainScale  the owning cue's effective gain (0..1) — multiplied into peak.
 * @param jitterSemis pitch jitter already sampled by the caller (0 = none) — kept as a
 *        param (not Math.random here) so the mapping stays pure + testable.
 */
export function toneToParams(desc: ToneDesc, gainScale = 1, jitterSemis = 0): ToneParams {
  const freq = applyPitchJitter(desc.freq, jitterSemis);
  const sweepTo = desc.sweepTo ?? Math.max(freq * SWEEP_RATIO, SWEEP_FLOOR_HZ);
  return {
    freq,
    sweepTo,
    osc: desc.osc,
    durationSec: desc.durationMs / 1000,
    peakGain: (desc.gain ?? 1) * gainScale * LEGACY_GAIN_HEADROOM,
    endGain: DECAY_FLOOR_GAIN,
  };
}
